/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Cost Analytics SQL Queries
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// SQL strings backing the /dashboards/cost-analytics page. Each query
/// targets `SNOWFLAKE.ACCOUNT_USAGE` views (account-wide, ~45min – 3hr latency).
///
/// Credit → dollar conversion is centralized in `CREDIT_PRICE_USD`. Override per
/// account if you have a non-standard rate (Enterprise / Business Critical /
/// custom pricing). Storage is priced at $/TB-month and data transfer at $/GB.
///
/// Every column is double-quoted with a camelCase alias so the parsed
/// `useSnowflake` response keys match the existing component prop contract
/// without any post-processing.

// //////////////////////////////////////////////////////////////////
// 💬 Pricing constants
// //////////////////////////////////////////////////////////////////
// Standard edition AWS US-East-1 list price as of 2025: ~$3.00 / credit.
// Bump to $4 (Enterprise) / $5 (Business Critical) per the customer's contract.
export const CREDIT_PRICE_USD = 3.0
// Average on-demand storage list price (~$23 / TB / month). Used for the
// trend chart's storage line — we report a daily slice of the monthly cost.
export const STORAGE_PRICE_PER_TB_MONTH_USD = 23.0
// Egress data transfer (~$0.09 / GB AWS inter-region). Coarse default.
export const DATA_TRANSFER_PRICE_PER_GB_USD = 0.09

// //////////////////////////////////////////////////////////////////
// 💬 Helpers
// //////////////////////////////////////////////////////////////////
/** Convert a UI range string (`'7d' | '30d' | '90d' | '1y'`) → integer days.
 *
 *  Retained for any callers still passing the legacy string form. New code
 *  should pass a {start, end} date range and use `dateRangeClause()` for SQL
 *  inclusion. */
export function rangeToDays(range: string = '30d'): number {
  switch (range) {
    case '7d': return 7
    case '30d': return 30
    case '90d': return 90
    case '1y': return 365
    default: return 30
  }
}

/** Convert a JS Date to Snowflake's literal timestamp form (no TZ; the
 *  ACCOUNT_USAGE views interpret these as TIMESTAMP_LTZ in account TZ). */
function toSqlTimestamp(d: Date): string {
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '')
}

/** Build a WHERE-clause fragment that filters `column` to a date range.
 *
 *  Returns: `column >= TO_TIMESTAMP('2025-04-01 00:00:00')
 *            AND column <  TO_TIMESTAMP('2025-04-30 23:59:59')`
 *
 *  Single-quoted timestamps are safe — JS produces ISO-8601 strings that
 *  contain no quote characters, so no SQL-injection concern from passing
 *  Date objects through. The end is exclusive in spirit (we let the
 *  caller decide whether to subtract a tick) — most callers want
 *  inclusive ends, so we keep `<=`.
 */
export interface DateRangeArg { start: Date; end: Date }
export function dateRangeClause(column: string, range: DateRangeArg): string {
  return `${column} >= TO_TIMESTAMP('${toSqlTimestamp(range.start)}')
    AND ${column} <= TO_TIMESTAMP('${toSqlTimestamp(range.end)}')`
}

/** Same as dateRangeClause but for DATE columns (e.g. STORAGE_USAGE.USAGE_DATE).
 *  We compare against TO_DATE so we don't mix a DATE column with a TIMESTAMP. */
export function dateOnlyRangeClause(column: string, range: DateRangeArg): string {
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return `${column} >= TO_DATE('${fmt(range.start)}')
    AND ${column} <= TO_DATE('${fmt(range.end)}')`
}

/** Number of days the range spans, rounded up. Used for the GENERATOR
 *  rowcount in trendSql. */
function rangeDays(range: DateRangeArg): number {
  const ms = range.end.getTime() - range.start.getTime()
  return Math.max(1, Math.ceil(ms / 86_400_000) + 1) // +1 so end-of-day inclusive
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost Overview - one-row aggregates for the four KPI cards.
// //////////////////////////////////////////////////////////////////
/// Source: SNOWFLAKE.ACCOUNT_USAGE.METERING_DAILY_HISTORY (pre-aggregated daily)
/// filtered to SERVICE_TYPE = 'WAREHOUSE_METERING'. Compared with the prior
/// implementation against WAREHOUSE_METERING_HISTORY (one row / warehouse /
/// hour), this is dramatically fewer rows scanned for the same numbers, since
/// the dashboard only needs daily totals here. Per-warehouse / per-user / hourly
/// queries below still use the granular views — daily can't answer those.
///
/// All metrics respect the chosen `range`:
///   - totalMonthlyCost          → total cost over range
///   - totalMonthlyCostChange    → % change vs the SAME-LENGTH period immediately before range.start
///   - averageDailyCost          → range total / number of days in range
///   - averageDailyCostTrend     → second half of range vs first half
///                                 (returns 'up' / 'down' / 'stable')
///   - costPerQuery              → range cost / range query count
///   - projectedMonthlyCost      → 30-day burn-rate projection from the range
///                                 (range_total / days_in_range × 30)
///
/// The legacy field names (totalMonthlyCost / projectedMonthlyCost) are
/// preserved so consuming components don't need template changes.
/// Sparkline is fetched separately (see overviewSparklineSqlFn).
export function overviewKpiSql(range?: DateRangeArg): string {
  const r = range ?? defaultMonthRange()
  // Same-length period immediately before range.start, used for the
  // headline % change number. e.g. range = last 30 days → prior is the
  // 30 days before that.
  const ms = Math.max(86_400_000, r.end.getTime() - r.start.getTime())
  const priorStart = new Date(r.start.getTime() - ms)
  const priorEnd   = new Date(r.start.getTime() - 1)
  const prior: DateRangeArg = { start: priorStart, end: priorEnd }

  // First / second half split for the trend arrow. Half-points are
  // computed in JS so the SQL stays readable.
  const midpoint = new Date(r.start.getTime() + ms / 2)
  const firstHalf:  DateRangeArg = { start: r.start, end: midpoint }
  const secondHalf: DateRangeArg = { start: midpoint, end: r.end }

  const days = rangeDays(r)

  return `
WITH range_daily AS (
  SELECT
    USAGE_DATE,
    CREDITS_USED_COMPUTE + CREDITS_USED_CLOUD_SERVICES AS credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.METERING_DAILY_HISTORY
  WHERE SERVICE_TYPE = 'WAREHOUSE_METERING'
    AND ${dateOnlyRangeClause('USAGE_DATE', { start: priorStart, end: r.end })}
),
range_totals AS (
  SELECT
    SUM(IFF(${dateOnlyRangeClause('USAGE_DATE', r)},     credits, 0))            AS range_credits,
    SUM(IFF(${dateOnlyRangeClause('USAGE_DATE', prior)}, credits, 0))            AS prior_credits,
    SUM(IFF(${dateOnlyRangeClause('USAGE_DATE', firstHalf)},  credits, 0))       AS first_half_credits,
    SUM(IFF(${dateOnlyRangeClause('USAGE_DATE', secondHalf)}, credits, 0))       AS second_half_credits
  FROM range_daily
),
qh AS (
  SELECT COUNT(*) AS qc
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
  WHERE ${dateRangeClause('START_TIME', r)}
)
SELECT
  COALESCE(range_totals.range_credits, 0) * ${CREDIT_PRICE_USD}                                                AS "totalMonthlyCost",
  CASE
    WHEN COALESCE(range_totals.prior_credits, 0) = 0 THEN 0
    ELSE ROUND(((range_totals.range_credits - range_totals.prior_credits)
               / range_totals.prior_credits) * 100, 2)
  END                                                                                                          AS "totalMonthlyCostChange",
  ROUND((COALESCE(range_totals.range_credits, 0) / ${days}) * ${CREDIT_PRICE_USD}, 2)                          AS "averageDailyCost",
  CASE
    WHEN COALESCE(range_totals.first_half_credits, 0) = 0 THEN 'stable'
    WHEN range_totals.second_half_credits > range_totals.first_half_credits * 1.05 THEN 'up'
    WHEN range_totals.second_half_credits < range_totals.first_half_credits * 0.95 THEN 'down'
    ELSE 'stable'
  END                                                                                                          AS "averageDailyCostTrend",
  CASE
    WHEN COALESCE(qh.qc, 0) = 0 THEN 0
    ELSE ROUND((range_totals.range_credits * ${CREDIT_PRICE_USD}) / qh.qc, 4)
  END                                                                                                          AS "costPerQuery",
  ROUND((COALESCE(range_totals.range_credits, 0) / ${days}) * 30 * ${CREDIT_PRICE_USD}, 2)                     AS "projectedMonthlyCost"
FROM range_totals, qh
`
}

/** Default range — current calendar month. Used by overview KPI / sparkline
 *  when callers haven't yet been migrated to pass an explicit range. */
function defaultMonthRange(): DateRangeArg {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  return { start, end: now }
}

// //////////////////////////////////////////////////////////////////
// 💬 Warehouse Spend (Last 6 Months) - per-warehouse monthly cost
// for the home page's stacked bar chart.
// //////////////////////////////////////////////////////////////////
/// Returns one row per (month, warehouse) over the trailing 6 months.
/// `month` comes back as canonical 'YYYY-MM' so the JS pivot can sort
/// it deterministically; the dashboard converts to display form.
///
/// We exclude `CLOUD_SERVICES_ONLY` — that's Snowflake's internal
/// bucket for cloud-services credits net of the 10% adjustment, not a
/// warehouse the user controls. Including it adds a permanently-near-
/// zero segment that clutters the legend without telling the user
/// anything actionable.
export const spendByWarehouse6mSql = `
SELECT
  TO_CHAR(DATE_TRUNC('MONTH', START_TIME), 'YYYY-MM') AS "month",
  WAREHOUSE_NAME                                       AS "warehouseName",
  ROUND(SUM(CREDITS_USED) * ${CREDIT_PRICE_USD}, 2)    AS "cost"
FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
WHERE START_TIME >= DATEADD(MONTH, -5, DATE_TRUNC('MONTH', CURRENT_DATE()))
  AND WAREHOUSE_NAME IS NOT NULL
  AND WAREHOUSE_NAME != 'CLOUD_SERVICES_ONLY'
GROUP BY 1, 2
ORDER BY 1, "cost" DESC
`

/** Sparkline — one row per day from METERING_DAILY_HISTORY across the
 *  selected range. Component decides how many points to render; we just
 *  return the daily series. */
export function overviewSparklineSql(range?: DateRangeArg): string {
  const r = range ?? defaultMonthRange()
  return `
SELECT
  TO_CHAR(USAGE_DATE, 'YYYY-MM-DD')                                                  AS "date",
  ROUND((CREDITS_USED_COMPUTE + CREDITS_USED_CLOUD_SERVICES) * ${CREDIT_PRICE_USD}, 2) AS "cost"
FROM SNOWFLAKE.ACCOUNT_USAGE.METERING_DAILY_HISTORY
WHERE SERVICE_TYPE = 'WAREHOUSE_METERING'
  AND ${dateOnlyRangeClause('USAGE_DATE', r)}
ORDER BY USAGE_DATE
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost Trend - daily breakdown for the stacked area chart.
// //////////////////////////////////////////////////////////////////
/// Compute & Cloud Services come from METERING_DAILY_HISTORY (pre-aggregated,
/// already split into CREDITS_USED_COMPUTE / CREDITS_USED_CLOUD_SERVICES).
/// Storage uses STORAGE_USAGE (daily by definition). Data Transfer uses
/// DATA_TRANSFER_HISTORY (per-event, but small volume in most accounts).
///
/// Accepts either a {start, end} range (preferred) or an integer days-back
/// for legacy callers. We anchor the GENERATOR row count to the range span
/// so the chart spans exactly the picked dates with no gap or overshoot.
export function trendSql(rangeArg: DateRangeArg | number): string {
  const range = typeof rangeArg === 'number'
    ? { start: new Date(Date.now() - rangeArg * 86_400_000), end: new Date() }
    : rangeArg
  const days = rangeDays(range)
  return `
WITH days AS (
  SELECT DATE_TRUNC('DAY', DATEADD(DAY, -SEQ4(), TO_DATE('${range.end.toISOString().slice(0,10)}'))) AS USAGE_DAY
  FROM TABLE(GENERATOR(ROWCOUNT => ${days}))
),
compute_daily AS (
  SELECT
    USAGE_DATE                            AS USAGE_DAY,
    SUM(CREDITS_USED_COMPUTE)             AS compute_credits,
    SUM(CREDITS_USED_CLOUD_SERVICES)      AS cloud_services_credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.METERING_DAILY_HISTORY
  WHERE SERVICE_TYPE = 'WAREHOUSE_METERING'
    AND ${dateOnlyRangeClause('USAGE_DATE', range)}
  GROUP BY USAGE_DATE
),
storage_daily AS (
  SELECT
    DATE_TRUNC('DAY', USAGE_DATE) AS USAGE_DAY,
    -- STORAGE_BYTES + STAGE_BYTES + FAILSAFE_BYTES is the day's point-in-time
    -- byte total. Convert to TB and prorate to a single day's slice of the
    -- monthly TB price ($/TB/month ÷ 30).
    (AVG(STORAGE_BYTES + STAGE_BYTES + FAILSAFE_BYTES) / POWER(1024, 4))
      * (${STORAGE_PRICE_PER_TB_MONTH_USD} / 30)
      AS storage_cost_usd
  FROM SNOWFLAKE.ACCOUNT_USAGE.STORAGE_USAGE
  WHERE ${dateOnlyRangeClause('USAGE_DATE', range)}
  GROUP BY USAGE_DAY
),
transfer_daily AS (
  SELECT
    DATE_TRUNC('DAY', START_TIME) AS USAGE_DAY,
    (SUM(BYTES_TRANSFERRED) / POWER(1024, 3)) * ${DATA_TRANSFER_PRICE_PER_GB_USD} AS transfer_cost_usd
  FROM SNOWFLAKE.ACCOUNT_USAGE.DATA_TRANSFER_HISTORY
  WHERE ${dateRangeClause('START_TIME', range)}
  GROUP BY USAGE_DAY
)
SELECT
  TO_CHAR(d.USAGE_DAY, 'YYYY-MM-DD')                                              AS "date",
  ROUND(COALESCE(c.compute_credits, 0)        * ${CREDIT_PRICE_USD}, 2)           AS "computeCost",
  ROUND(COALESCE(s.storage_cost_usd, 0), 2)                                       AS "storageCost",
  ROUND(COALESCE(t.transfer_cost_usd, 0), 2)                                      AS "dataTransferCost",
  ROUND(COALESCE(c.cloud_services_credits, 0) * ${CREDIT_PRICE_USD}, 2)           AS "cloudServicesCost"
FROM days d
LEFT JOIN compute_daily  c ON c.USAGE_DAY = d.USAGE_DAY
LEFT JOIN storage_daily  s ON s.USAGE_DAY = d.USAGE_DAY
LEFT JOIN transfer_daily t ON t.USAGE_DAY = d.USAGE_DAY
ORDER BY d.USAGE_DAY
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost By Warehouse - top warehouses over the selected range.
// //////////////////////////////////////////////////////////////////
/// Pulls warehouse size from the most recent QUERY_HISTORY row that ran on
/// the warehouse (WAREHOUSE_SIZE there is the size at execution time).
/// Default range is month-to-date when no range is provided — preserves
/// prior behavior for any caller that hasn't been migrated yet.
export function byWarehouseSql(range?: DateRangeArg): string {
  const wmhFilter = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATE_TRUNC('MONTH', CURRENT_DATE())`
  const qhFilter  = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATE_TRUNC('MONTH', CURRENT_DATE())`
  return `
WITH wh_costs AS (
  SELECT
    WAREHOUSE_NAME,
    SUM(CREDITS_USED) AS credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
  WHERE ${wmhFilter}
    AND WAREHOUSE_NAME IS NOT NULL
  GROUP BY WAREHOUSE_NAME
),
wh_total AS (
  SELECT SUM(credits) AS total_credits FROM wh_costs
),
wh_query_counts AS (
  SELECT
    WAREHOUSE_NAME,
    COUNT(*) AS query_count,
    MAX_BY(WAREHOUSE_SIZE, START_TIME) AS latest_size
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
  WHERE ${qhFilter}
    AND WAREHOUSE_NAME IS NOT NULL
  GROUP BY WAREHOUSE_NAME
)
SELECT
  c.WAREHOUSE_NAME                                                       AS "name",
  COALESCE(qc.latest_size, 'Unknown')                                    AS "size",
  ROUND(c.credits * ${CREDIT_PRICE_USD}, 2)                              AS "cost",
  CASE WHEN t.total_credits = 0 THEN 0
       ELSE ROUND((c.credits / t.total_credits) * 100, 1) END             AS "percentOfTotal",
  COALESCE(qc.query_count, 0)                                            AS "queryCount"
FROM wh_costs c
CROSS JOIN wh_total t
LEFT JOIN wh_query_counts qc ON qc.WAREHOUSE_NAME = c.WAREHOUSE_NAME
ORDER BY "cost" DESC
LIMIT 50
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost By User - per-user query attribution over the chosen range.
// //////////////////////////////////////////////////////////////////
/// Snowflake doesn't natively split warehouse credits per user. We approximate
/// by prorating each warehouse's credits to its users by execution time share,
/// then add CREDITS_USED_CLOUD_SERVICES (which IS attributed per-query).
export function byUserSql(range?: DateRangeArg): string {
  const qhFilter  = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATE_TRUNC('MONTH', CURRENT_DATE())`
  const wmhFilter = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATE_TRUNC('MONTH', CURRENT_DATE())`
  return `
WITH user_exec AS (
  SELECT
    USER_NAME,
    WAREHOUSE_NAME,
    COUNT(*)                                AS query_count,
    SUM(EXECUTION_TIME)                     AS exec_ms,
    SUM(COALESCE(CREDITS_USED_CLOUD_SERVICES, 0)) AS cs_credits,
    MAX(START_TIME)                         AS last_active_ts
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
  WHERE ${qhFilter}
    AND USER_NAME IS NOT NULL
  GROUP BY USER_NAME, WAREHOUSE_NAME
),
wh_exec_totals AS (
  SELECT WAREHOUSE_NAME, SUM(exec_ms) AS total_exec_ms FROM user_exec GROUP BY WAREHOUSE_NAME
),
wh_credits AS (
  SELECT WAREHOUSE_NAME, SUM(CREDITS_USED) AS credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
  WHERE ${wmhFilter}
    AND WAREHOUSE_NAME IS NOT NULL
  GROUP BY WAREHOUSE_NAME
),
attributed AS (
  SELECT
    ue.USER_NAME,
    SUM(ue.query_count)        AS query_count,
    SUM(ue.cs_credits)         AS cs_credits,
    -- prorate warehouse credits by exec-time share within each warehouse
    SUM(
      CASE WHEN COALESCE(wt.total_exec_ms, 0) = 0 OR wc.credits IS NULL THEN 0
           ELSE wc.credits * (ue.exec_ms / wt.total_exec_ms)
      END
    )                          AS attributed_credits,
    MAX(ue.last_active_ts)     AS last_active_ts
  FROM user_exec ue
  LEFT JOIN wh_exec_totals wt ON wt.WAREHOUSE_NAME = ue.WAREHOUSE_NAME
  LEFT JOIN wh_credits     wc ON wc.WAREHOUSE_NAME = ue.WAREHOUSE_NAME
  GROUP BY ue.USER_NAME
),
user_emails AS (
  -- ACCOUNT_USAGE.USERS exposes login_name + email; align by user name (uppercased)
  SELECT NAME, EMAIL FROM SNOWFLAKE.ACCOUNT_USAGE.USERS WHERE DELETED_ON IS NULL
)
SELECT
  ROW_NUMBER() OVER (ORDER BY a.USER_NAME)                                              AS "id",
  a.USER_NAME                                                                            AS "name",
  COALESCE(ue.EMAIL, '')                                                                 AS "email",
  a.query_count                                                                          AS "totalQueries",
  ROUND((a.attributed_credits + a.cs_credits) * ${CREDIT_PRICE_USD}, 2)                  AS "totalCost",
  CASE WHEN a.query_count = 0 THEN 0
       ELSE ROUND(((a.attributed_credits + a.cs_credits) * ${CREDIT_PRICE_USD}) / a.query_count, 4)
  END                                                                                    AS "avgCostPerQuery",
  TO_CHAR(a.last_active_ts, 'YYYY-MM-DD"T"HH24:MI:SS"Z"')                               AS "lastActive"
FROM attributed a
LEFT JOIN user_emails ue ON ue.NAME = a.USER_NAME
ORDER BY "totalCost" DESC
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost By User Over Time — for the user-management page chart.
// //////////////////////////////////////////////////////////////////
/// Same prorating logic as `byUserSql` (warehouse credits split among
/// users by execution-time share, plus per-query CLOUD_SERVICES
/// credits) but bucketed by day instead of summed once. Output is a
/// long-form table — one row per (day × user) — so the chart can pivot
/// it to top-N stacked series client-side without needing to know the
/// users up front. The "Other" bucket aggregation lives in the
/// component to keep the SQL minimal.
///
/// Granularity: always daily. Coarser bucketing (weekly, monthly) is
/// noisy at small scales and the chart can downsample if needed; for
/// >90 day ranges we'd want to switch to weekly, but that's a future
/// enhancement.
export function byUserOverTimeSql(range?: DateRangeArg): string {
  const qhFilter  = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATEADD(day, -30, CURRENT_TIMESTAMP())`
  const wmhFilter = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATEADD(day, -30, CURRENT_TIMESTAMP())`
  return `
WITH user_exec_daily AS (
  SELECT
    DATE_TRUNC('day', START_TIME)                  AS day,
    USER_NAME,
    WAREHOUSE_NAME,
    SUM(EXECUTION_TIME)                            AS exec_ms,
    SUM(COALESCE(CREDITS_USED_CLOUD_SERVICES, 0))  AS cs_credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
  WHERE ${qhFilter}
    AND USER_NAME IS NOT NULL
  GROUP BY 1, 2, 3
),
wh_exec_daily AS (
  SELECT day, WAREHOUSE_NAME, SUM(exec_ms) AS total_exec_ms
  FROM user_exec_daily
  GROUP BY 1, 2
),
wh_credits_daily AS (
  SELECT
    DATE_TRUNC('day', START_TIME)  AS day,
    WAREHOUSE_NAME,
    SUM(CREDITS_USED)              AS credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
  WHERE ${wmhFilter}
    AND WAREHOUSE_NAME IS NOT NULL
  GROUP BY 1, 2
)
SELECT
  TO_CHAR(ued.day, 'YYYY-MM-DD')                                                AS "day",
  -- Aliased as "entity" so the same chart component can render either
  -- per-user or per-role rows without caring which dimension produced
  -- the data. The role-cost variant below uses the same alias.
  ued.USER_NAME                                                                  AS "entity",
  ROUND(
    SUM(
      CASE WHEN COALESCE(wed.total_exec_ms, 0) = 0 OR wcd.credits IS NULL THEN 0
           ELSE wcd.credits * (ued.exec_ms / wed.total_exec_ms)
      END
    ) * ${CREDIT_PRICE_USD}
    + SUM(ued.cs_credits) * ${CREDIT_PRICE_USD},
    2
  )                                                                              AS "cost"
FROM user_exec_daily ued
LEFT JOIN wh_exec_daily    wed ON wed.day = ued.day AND wed.WAREHOUSE_NAME = ued.WAREHOUSE_NAME
LEFT JOIN wh_credits_daily wcd ON wcd.day = ued.day AND wcd.WAREHOUSE_NAME = ued.WAREHOUSE_NAME
GROUP BY ued.day, ued.USER_NAME
HAVING "cost" > 0
ORDER BY ued.day ASC, "cost" DESC
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost By Role Over Time — for the role-management page chart.
// //////////////////////////////////////////////////////////////////
/// Mirror of byUserOverTimeSql but bucketed by ROLE_NAME instead of
/// USER_NAME. Snowflake's QUERY_HISTORY records the active role per
/// query, so role-attribution is direct (the user runs SET ROLE ...
/// before issuing queries; whatever role was active gets the cost).
///
/// WAREHOUSE_METERING_HISTORY has no role column, so we apply the
/// same exec-time-prorating trick: each warehouse's daily credits
/// are split among the roles that ran queries on it that day,
/// weighted by execution time.
///
/// Output schema matches byUserOverTimeSql ({day, entity, cost}) so
/// the same chart component can render either dimension.
export function byRoleOverTimeSql(range?: DateRangeArg): string {
  const qhFilter  = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATEADD(day, -30, CURRENT_TIMESTAMP())`
  const wmhFilter = range ? dateRangeClause('START_TIME', range) : `START_TIME >= DATEADD(day, -30, CURRENT_TIMESTAMP())`
  return `
WITH role_exec_daily AS (
  SELECT
    DATE_TRUNC('day', START_TIME)                  AS day,
    ROLE_NAME,
    WAREHOUSE_NAME,
    SUM(EXECUTION_TIME)                            AS exec_ms,
    SUM(COALESCE(CREDITS_USED_CLOUD_SERVICES, 0))  AS cs_credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
  WHERE ${qhFilter}
    AND ROLE_NAME IS NOT NULL
  GROUP BY 1, 2, 3
),
wh_exec_daily AS (
  SELECT day, WAREHOUSE_NAME, SUM(exec_ms) AS total_exec_ms
  FROM role_exec_daily
  GROUP BY 1, 2
),
wh_credits_daily AS (
  SELECT
    DATE_TRUNC('day', START_TIME)  AS day,
    WAREHOUSE_NAME,
    SUM(CREDITS_USED)              AS credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
  WHERE ${wmhFilter}
    AND WAREHOUSE_NAME IS NOT NULL
  GROUP BY 1, 2
)
SELECT
  TO_CHAR(red.day, 'YYYY-MM-DD')                                                AS "day",
  red.ROLE_NAME                                                                  AS "entity",
  ROUND(
    SUM(
      CASE WHEN COALESCE(wed.total_exec_ms, 0) = 0 OR wcd.credits IS NULL THEN 0
           ELSE wcd.credits * (red.exec_ms / wed.total_exec_ms)
      END
    ) * ${CREDIT_PRICE_USD}
    + SUM(red.cs_credits) * ${CREDIT_PRICE_USD},
    2
  )                                                                              AS "cost"
FROM role_exec_daily red
LEFT JOIN wh_exec_daily    wed ON wed.day = red.day AND wed.WAREHOUSE_NAME = red.WAREHOUSE_NAME
LEFT JOIN wh_credits_daily wcd ON wcd.day = red.day AND wcd.WAREHOUSE_NAME = red.WAREHOUSE_NAME
GROUP BY red.day, red.ROLE_NAME
HAVING "cost" > 0
ORDER BY red.day ASC, "cost" DESC
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost Anomalies - daily warehouse credit spend > 2σ above mean.
// //////////////////////////////////////////////////////////////////
/// Each (warehouse, day) within the chosen range is compared against that
/// warehouse's mean/stddev over the prior 14 days. We extend the SQL's
/// data window 14 days BEFORE the range start so the rolling baseline is
/// fully populated for the first visible day.
export function anomaliesSql(range?: DateRangeArg): string {
  // Anomaly evaluation window: the visible report range. Default = last 30d.
  const r = range ?? { start: new Date(Date.now() - 30 * 86_400_000), end: new Date() }
  // Baseline window: pull an extra 14 days BEFORE so the rolling 14d
  // mean/stddev is populated for the very first visible day.
  const baselineStart = new Date(r.start.getTime() - 14 * 86_400_000)
  const baselineRange = { start: baselineStart, end: r.end }
  const visibleClause = dateRangeClause('s.USAGE_DAY', r)
  return `
WITH wh_daily AS (
  SELECT
    DATE_TRUNC('DAY', START_TIME) AS USAGE_DAY,
    WAREHOUSE_NAME,
    SUM(CREDITS_USED) AS credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
  WHERE ${dateRangeClause('START_TIME', baselineRange)}
    AND WAREHOUSE_NAME IS NOT NULL
  GROUP BY 1, 2
),
with_stats AS (
  SELECT
    USAGE_DAY,
    WAREHOUSE_NAME,
    credits,
    AVG(credits)    OVER (PARTITION BY WAREHOUSE_NAME ORDER BY USAGE_DAY ROWS BETWEEN 14 PRECEDING AND 1 PRECEDING) AS rolling_mean,
    STDDEV(credits) OVER (PARTITION BY WAREHOUSE_NAME ORDER BY USAGE_DAY ROWS BETWEEN 14 PRECEDING AND 1 PRECEDING) AS rolling_stddev
  FROM wh_daily
),
top_user_per_day AS (
  -- pick the heaviest user per (warehouse, day) so the alert can attribute to a person
  SELECT
    DATE_TRUNC('DAY', START_TIME) AS USAGE_DAY,
    WAREHOUSE_NAME,
    USER_NAME,
    SUM(EXECUTION_TIME) AS exec_ms,
    ROW_NUMBER() OVER (PARTITION BY DATE_TRUNC('DAY', START_TIME), WAREHOUSE_NAME ORDER BY SUM(EXECUTION_TIME) DESC) AS rn
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY
  WHERE ${dateRangeClause('START_TIME', r)}
    AND WAREHOUSE_NAME IS NOT NULL
    AND USER_NAME IS NOT NULL
  GROUP BY 1, 2, 3
)
SELECT
  -- Qualify WAREHOUSE_NAME — after the LEFT JOIN it exists in both s and t,
  -- and Snowflake refuses to guess which alias to bind the unqualified name to.
  CONCAT('anom_', s.WAREHOUSE_NAME, '_', TO_CHAR(s.USAGE_DAY, 'YYYYMMDD'))       AS "id",
  TO_CHAR(s.USAGE_DAY, 'YYYY-MM-DD"T"HH24:MI:SS"Z"')                              AS "timestamp",
  'spike'                                                                          AS "type",
  CASE
    WHEN (s.credits - s.rolling_mean) / NULLIF(s.rolling_stddev, 0) >= 3 THEN 'critical'
    WHEN (s.credits - s.rolling_mean) / NULLIF(s.rolling_stddev, 0) >= 2 THEN 'warning'
    ELSE 'info'
  END                                                                              AS "severity",
  s.WAREHOUSE_NAME                                                                 AS "warehouse",
  COALESCE(t.USER_NAME, '')                                                        AS "user",
  ROUND(s.credits        * ${CREDIT_PRICE_USD}, 2)                                 AS "cost",
  ROUND(s.rolling_mean   * ${CREDIT_PRICE_USD}, 2)                                 AS "expectedCost",
  CONCAT(
    'Cost spike on ', s.WAREHOUSE_NAME, ' — ',
    ROUND(((s.credits - s.rolling_mean) / NULLIF(s.rolling_mean, 0)) * 100, 0),
    '% above 14d baseline (',
    ROUND((s.credits - s.rolling_mean) / NULLIF(s.rolling_stddev, 0), 1), 'σ).'
  )                                                                                AS "description"
FROM with_stats s
LEFT JOIN top_user_per_day t
  ON t.USAGE_DAY      = s.USAGE_DAY
 AND t.WAREHOUSE_NAME = s.WAREHOUSE_NAME
 AND t.rn             = 1
WHERE ${visibleClause}
  AND s.rolling_stddev IS NOT NULL
  AND s.rolling_stddev > 0
  AND (s.credits - s.rolling_mean) / s.rolling_stddev >= 2
ORDER BY s.USAGE_DAY DESC, s.credits DESC
LIMIT 25
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost Heatmap - day-of-week × hour-of-day over the chosen range.
// //////////////////////////////////////////////////////////////////
/// We use DAYOFWEEKISO (1=Mon … 7=Sun — no underscore in the function name,
/// despite the spelling) and map to the component's expected ordering
/// (0=Sunday … 6=Saturday) via MOD(iso, 7) — this is independent of the
/// session WEEK_START parameter, unlike DAYOFWEEK.
export function heatmapSql(range?: DateRangeArg): string {
  const r = range ?? { start: new Date(Date.now() - 30 * 86_400_000), end: new Date() }
  return `
WITH base AS (
  SELECT
    MOD(DAYOFWEEKISO(START_TIME), 7) AS dow,
    HOUR(START_TIME)                 AS hr,
    CREDITS_USED
  FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
  WHERE ${dateRangeClause('START_TIME', r)}
)
SELECT
  d.dow                                      AS "dayOfWeek",
  h.hr                                       AS "hour",
  ROUND(COALESCE(SUM(b.CREDITS_USED), 0) * ${CREDIT_PRICE_USD}, 2) AS "cost"
FROM (SELECT 0 AS dow UNION ALL SELECT 1 UNION ALL SELECT 2 UNION ALL SELECT 3
       UNION ALL SELECT 4 UNION ALL SELECT 5 UNION ALL SELECT 6) d
CROSS JOIN (
  SELECT SEQ4() AS hr FROM TABLE(GENERATOR(ROWCOUNT => 24))
) h
LEFT JOIN base b ON b.dow = d.dow AND b.hr = h.hr
GROUP BY d.dow, h.hr
ORDER BY d.dow, h.hr
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Cost Recommendations - rule-based optimization opportunities.
// //////////////////////////////////////////////////////////////////
/// Each rule emits zero or more rows with the component's expected shape:
/// id, type, title, description, potentialSavings, action, priority. We
/// UNION ALL the rule sets so a single query feeds the component.
///
/// All three rules now share the dashboard's selected `range`:
///   - idle      → warehouses billing credits over range with zero queries over range
///   - oversized → avg query exec time across range
///   - long_running → single queries > 5min within range
///
/// Component description text builds from the active range's day count so
/// "no queries in the last X days" stays accurate after a filter change.
export function recommendationsSql(range?: DateRangeArg): string {
  const r = range ?? { start: new Date(Date.now() - 7 * 86_400_000), end: new Date() }
  const days = rangeDays(r)
  // Used in the human-readable description text. Plural-aware so a
  // "Today" preset reads "no queries in the last 1 day".
  const daysLabel = `${days} day${days === 1 ? '' : 's'}`
  return `
WITH idle AS (
  -- warehouses billing credits in range but with zero queries in range
  SELECT
    wmh.WAREHOUSE_NAME,
    SUM(wmh.CREDITS_USED) AS wasted_credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY wmh
  WHERE ${dateRangeClause('wmh.START_TIME', r)}
    AND wmh.WAREHOUSE_NAME IS NOT NULL
    AND NOT EXISTS (
      SELECT 1 FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY qh
      WHERE qh.WAREHOUSE_NAME = wmh.WAREHOUSE_NAME
        AND ${dateRangeClause('qh.START_TIME', r)}
    )
  GROUP BY wmh.WAREHOUSE_NAME
  HAVING SUM(wmh.CREDITS_USED) > 0.1
),
oversized AS (
  -- warehouses where avg query exec time is < 30s, indicates over-provisioning
  SELECT
    qh.WAREHOUSE_NAME,
    MAX_BY(qh.WAREHOUSE_SIZE, qh.START_TIME) AS current_size,
    AVG(qh.EXECUTION_TIME) / 1000.0          AS avg_exec_seconds,
    COUNT(*)                                 AS query_count,
    SUM(wmh_credits.credits)                 AS recent_credits
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY qh
  LEFT JOIN (
    SELECT WAREHOUSE_NAME, SUM(CREDITS_USED) AS credits
    FROM SNOWFLAKE.ACCOUNT_USAGE.WAREHOUSE_METERING_HISTORY
    WHERE ${dateRangeClause('START_TIME', r)}
    GROUP BY WAREHOUSE_NAME
  ) wmh_credits ON wmh_credits.WAREHOUSE_NAME = qh.WAREHOUSE_NAME
  WHERE ${dateRangeClause('qh.START_TIME', r)}
    AND qh.WAREHOUSE_NAME IS NOT NULL
    AND qh.WAREHOUSE_SIZE NOT IN ('X-Small', 'XSMALL')   -- can't go smaller than X-Small
  GROUP BY qh.WAREHOUSE_NAME
  HAVING AVG(qh.EXECUTION_TIME) / 1000.0 < 30
     AND COUNT(*) > 100
),
long_running AS (
  SELECT
    qh.QUERY_ID,
    qh.USER_NAME,
    qh.WAREHOUSE_NAME,
    qh.WAREHOUSE_SIZE,
    qh.EXECUTION_TIME / 1000.0 / 60                                                AS exec_minutes,
    -- approximate cost: warehouse_size_credits/hour × elapsed_hours
    -- standard credit/hr per size: XS=1, S=2, M=4, L=8, XL=16, 2XL=32, 3XL=64, 4XL=128
    DECODE(qh.WAREHOUSE_SIZE,
      'X-Small', 1, 'XSMALL', 1,
      'Small',   2, 'SMALL',   2,
      'Medium',  4, 'MEDIUM',  4,
      'Large',   8, 'LARGE',   8,
      'X-Large', 16, 'XLARGE', 16,
      '2X-Large', 32, '2XLARGE', 32,
      '3X-Large', 64, '3XLARGE', 64,
      '4X-Large', 128, '4XLARGE', 128,
      4
    ) * (qh.EXECUTION_TIME / 1000.0 / 3600.0) * ${CREDIT_PRICE_USD}                AS approx_cost
  FROM SNOWFLAKE.ACCOUNT_USAGE.QUERY_HISTORY qh
  WHERE ${dateRangeClause('qh.START_TIME', r)}
    AND qh.EXECUTION_TIME > 5 * 60 * 1000   -- > 5 minutes
)

-- Idle warehouses
SELECT
  CONCAT('idle_', WAREHOUSE_NAME)                                  AS "id",
  'idle_warehouse'                                                  AS "type",
  'Idle Warehouse Detected'                                         AS "title",
  CONCAT(WAREHOUSE_NAME, ' is billing credits but ran no queries in the last ${daysLabel}.') AS "description",
  ROUND(wasted_credits * ${CREDIT_PRICE_USD}, 2)                    AS "potentialSavings",
  'Reduce Auto-Resume / Suspend'                                    AS "action",
  'high'                                                            AS "priority"
FROM idle

UNION ALL

-- Oversized warehouses
SELECT
  CONCAT('oversized_', WAREHOUSE_NAME),
  'oversized',
  'Warehouse Oversized',
  CONCAT(
    WAREHOUSE_NAME, ' (', current_size, ') averages ',
    ROUND(avg_exec_seconds, 1), 's per query over ', query_count,
    ' queries — could likely run on a smaller size.'
  ),
  -- assume 30% savings if downsized one notch
  ROUND(COALESCE(recent_credits, 0) * 0.3 * ${CREDIT_PRICE_USD}, 2),
  'Downsize Warehouse',
  'medium'
FROM oversized

UNION ALL

-- Long-running queries
SELECT
  CONCAT('long_', QUERY_ID),
  'long_query',
  'Expensive Query Detected',
  CONCAT(
    'Query ', LEFT(QUERY_ID, 8), '… by ', USER_NAME, ' on ', WAREHOUSE_NAME,
    ' ran ', ROUND(exec_minutes, 1), ' min and cost ~$', ROUND(approx_cost, 2), '.'
  ),
  ROUND(approx_cost * 0.5, 2),  -- assume 50% reduction with optimization
  'Optimize Query',
  CASE WHEN approx_cost > 50 THEN 'high' WHEN approx_cost > 10 THEN 'medium' ELSE 'low' END
FROM long_running
WHERE approx_cost > 5
ORDER BY 5 DESC
LIMIT 50
`
}
// Note: a fourth rule for duplicate query patterns (same QUERY_HASH ≥10× in 24h)
// is intentionally deferred — QUERY_HASH support requires the 2024+ Snowflake
// release, and the value isn't always populated. Add it once we've confirmed
// QUERY_HASH is available in this account.
